import Component from '@glimmer/component'
import { service } from '@ember/service'
import { formatNumber } from 'district-ui-client/utils/giraffe/formatter'
import { colors } from '@colors'
import type GradeSetsService from 'district-ui-client/services/grade-sets'
import SeriesBar from 'district-ui-client/components/primitives/series-bar/component'
import { t } from 'ember-intl'
import type { SeriesBarOptions } from 'highcharts'
import type { CourseProgressByGradeData } from 'district-ui-client/utils/student-events/fetchers/course-progress-by-grade'

/**
 * Turns a student-events course_progress_by_grade response into a giraffe chart
 * using the dashboard-title and series-bar primitives.

 */

interface Signature {
  Args: {
    data: CourseProgressByGradeData
    product: LegacyProductRe | LegacyProductRex | LegacyProductMs
    precinct: LegacyPrecinctReading | LegacyPrecinctSpelling | LegacyPrecinctMyLessons | LegacyPrecinctLessons
  }
  Element: HTMLDivElement
}

export class ChartsDashboardCourseProgress extends Component<Signature> {
  @service gradeSets!: GradeSetsService

  /**
   * The series labels for the chart, formatted for Highcharts.
   * A list of formatted grade names.
   */
  get categories() {
    return this.args.data.grades.map((grade) => this.gradeSets.findByPosition(grade.grade_position)?.name ?? '-')
  }

  get color() {
    const { product, precinct } = this.args

    switch (`${product}.${precinct}`) {
      case 'maths.lessons':
        return colors.forestGreen300
      case 're.spelling':
      case 'rex.spelling':
        return colors.grapeyGreen300
      case 're.reading':
      case 'rex.my_lessons':
        return colors.oceanyBlue300
      default:
        return colors.oceanyBlue300
    }
  }

  /**
   * Bar data formatted for Highcharts
   */
  get bars(): SeriesBarOptions[] {
    const color = this.color
    const byGrade = this.args.data.grades
    return [
      {
        data: byGrade.map((grade) =>
          grade.student_count === 0 ? 0 : Math.round(grade.lesson_count / grade.student_count),
        ),
        color,
        type: 'bar',
      },
    ]
  }

  /**
   * Flag if data has only one grade
   */
  get singleGrade() {
    return this.args.data.grades.length === 1
  }

  /**
   * Total number of students across all grades
   * Calculates number of contributing students
   */
  get studentCount() {
    return formatNumber(this.args.data.student_count, 2)
  }

  <template>
    <div data-test-dashboard-course-progress class="flex h-full flex-col" ...attributes>
      <div data-test-dashboard-subtitle class="flex justify-between gap-4 text-xs">
        <span>
          {{#if this.singleGrade}}
            {{t "reporting.dashboardSubtitles.singleGrade.courseProgress"}}
          {{else}}
            {{t "reporting.dashboardSubtitles.multiGrade"}}
          {{/if}}
        </span>
        <span>
          {{t "reporting.dashboardSubtitles.studentCount" count=this.studentCount}}
        </span>
      </div>

      {{#if this.singleGrade}}
        <SeriesBar
          @data={{this.bars}}
          @categories={{this.categories}}
          @xAxisLabel={{t "grade.label"}}
          @yAxisLabel={{t "myLessons"}}
          @groupPadding={{0}}
          @barPadding={{0}}
          @chartMargin={{Array 55 0 104 0}}
          @yAxisOffset={{25}}
          @yAxisLineWidth={{1}}
          @yAxisGridLineWidth={{0}}
        />
      {{else}}
        <SeriesBar
          @data={{this.bars}}
          @categories={{this.categories}}
          @xAxisLabel={{t "grade.label"}}
          @yAxisLabel={{t "myLessons"}}
        />
      {{/if}}

    </div>
  </template>
}

export default ChartsDashboardCourseProgress
